/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';

import type { BasicFunction, BasicPromiseFunction } from '../../../helpers/jsHelpers/types';
import type { ChannelMap, NETGEM_API_CHANNEL } from '../../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_SERIES } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_METADATA_SCHEDULE_LOCATION } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { CombinedReducers } from '../../../redux/reducers';
import type { VideoPlayerMediaInfo } from '../implementation/types';

import { APPLICATION_ID } from '../../../helpers/applicationCustomization/types';
import { channelHasCatchup, channelHasStartover } from '../../../helpers/channel/helper';
import { formatSecondsForScreenReader, formatSecondsToHHMMSS, formatTimeFromSeconds } from '../../../helpers/dateTime/Format';
import Localizer from '../../../helpers/localization';
import MediaController from '../../../helpers/mediaSession/mediaController';
import { getBooleanSetting } from '../../../helpers/settings/settings';
import { Setting } from '../../../helpers/settings/types';
import { ExtendedItemType } from '../../../helpers/ui/item/types';
import { BroadcastStatus } from '../../../helpers/ui/location/Format';
import { getTitle, isEpisodeIndexInTitle, renderProgramParentalGuidance } from '../../../helpers/ui/metadata/Format';
import { Definition } from '../../../helpers/ui/metadata/Types';
import { ControlLevel } from '../../../helpers/ui/player';
import ContinuousWatching from '../../../helpers/viewingHistory/ContinuousWatching';
import { getLocationType } from '../../../libs/netgemLibrary/v8/helpers/Item';
import { ItemLocationKind, type NETGEM_API_V8_FEED_ITEM } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { TileConfigTileType } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import {
  PictoBigPlay,
  PictoCog,
  PictoCompress,
  PictoDownload,
  PictoExpand,
  PictoForward10,
  PictoForward30,
  PictoForward60,
  PictoInfo,
  PictoLeaf,
  PictoNext,
  PictoPause,
  PictoRecord,
  PictoReplay,
  PictoRewind10,
  PictoRewind30,
  PictoRewind60,
} from '../../pictos/Element';
import RecordButton, { ContextKind, DisplayMode } from '../../recordButton/RecordButton';
import StatusPicto from '../../statusPicto/StatusPicto';
import { ContentType, TIMESHIFT_THRESHOLD } from '../constantsAndTypes';
import './PlayerController.css';
import Options from './options/Options';
import EmptyProgressBar from './progressBar/empty/EmptyProgressBar';
import LiveProgressBar from './progressBar/live/LiveProgressBar';
import StandardProgressBar from './progressBar/standard/StandardProgressBar';
import PlayerVolume from './volume/Volume';
import ChannelZapper, { type ChannelZapperHandle } from './zapper/ChannelZapper';

// Timespan allowed to move cursor outside options element without closing it (in ms)
const OPTIONS_TIMEOUT = 500;

type ReduxPlayerControllerDispatchToPropsType = {};

type ReduxPlayerControllerReducerStateType = {
  +channels: ChannelMap,
  +isBitrateLimited: boolean,
  +isDebugModeEnabled: boolean,
  +isMaxBitrateAllowed: boolean,
};

type PlayerControllerPropType = {
  +audioMediaInfo: Array<VideoPlayerMediaInfo>,
  +bufferedTimeRanges: TimeRanges | null,
  +channelId: string | null,
  +channelImageUrl: string,
  +channelName: string,
  +contentType: ContentType,
  +continuousWatching: ContinuousWatching | null,
  +controlLevel: ControlLevel,
  +currentItem: NETGEM_API_V8_FEED_ITEM | null,
  +duration: number,
  +endMargin: number,
  +innerRef?: ((PlayerControllerView | null) => unknown) | null,
  +isBlackOutEnabled: boolean,
  +isControllerEnabled: boolean,
  +isInFullscreen: boolean,
  +isLiveRecording: boolean,
  +isMuted: boolean,
  +isPlaying: boolean,
  +isProgramInfoHidden: boolean,
  +isTimeshiftEnabled: boolean,
  +isTrailer: boolean,
  +isVisible: boolean,
  +item: NETGEM_API_V8_FEED_ITEM | null,
  +itemType: ExtendedItemType,
  +liveBufferLength: number,
  +location: NETGEM_API_V8_METADATA_SCHEDULE_LOCATION | null,
  +onChannelChanged: (channel: NETGEM_API_CHANNEL) => Promise<void>,
  +onDownloadClick: () => Promise<void> | void,
  +onEnterFullScreenClick: BasicFunction,
  +onExitFullScreenClick: BasicFunction,
  +onGoToLiveClick: BasicFunction | BasicPromiseFunction,
  +onInfoClick: BasicFunction,
  +onLiveProgressBarSeekChanged: (seekTime: number) => void,
  +onLiveRestart: BasicFunction,
  +onMouseEnter: BasicFunction,
  +onMouseLeave: BasicFunction,
  +onNextEpisodeClick: BasicFunction | BasicPromiseFunction,
  +onNewAudioTrackSelected: (index: number) => void,
  +onNewSubtitlesTrackSelected: (index: number) => void,
  +onPauseClick: BasicFunction | BasicPromiseFunction,
  +onPlayClick: BasicFunction | BasicPromiseFunction,
  +onSkipBackwardClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>) => void,
  +onSkipForwardClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>) => void,
  +onStandardProgressBarSeekChanged: (seekTime: number) => void,
  +onVolumeChanged: (volume: number) => void,
  +onVolumeClick: BasicFunction,
  +playheadPosition: number,
  +programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  +realEnd: number,
  +realStart: number,
  +selectedAudioMediaInfo: number,
  +selectedSubtitlesMediaInfo: number,
  +seriesEpisodeText: string | null,
  +seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null,
  +startMargin: number,
  +subtitlesMediaInfo: Array<VideoPlayerMediaInfo>,
  +tileType: TileConfigTileType,
  +title: string | null,
  +timeshift: number,
  +totalDuration: number,
  +userViewEndOffset: number,
  +userViewStartOffset: number,
  +videoQuality: Definition,
  +volume: number,
};

type CompletePlayerControllerPropType = {
  ...PlayerControllerPropType,
  ...ReduxPlayerControllerDispatchToPropsType,
  ...ReduxPlayerControllerReducerStateType,
};

type PlayerControllerStateType = {
  areOptionsVisible: boolean,
  isWaitingForDeleteConfirmation: boolean,
  isWaitingForRecordConfirmation: boolean,
  playingInMargin: boolean,
};

const InitialState = Object.freeze({
  areOptionsVisible: false,
  isWaitingForDeleteConfirmation: false,
  isWaitingForRecordConfirmation: false,
  playingInMargin: false,
});

class PlayerControllerView extends React.PureComponent<CompletePlayerControllerPropType, PlayerControllerStateType> {
  channelZapper: ChannelZapperHandle | null;

  optionsTimer: TimeoutID | null;

  constructor(props: CompletePlayerControllerPropType) {
    super(props);

    this.channelZapper = null;
    this.optionsTimer = null;

    this.state = { ...InitialState };
  }

  componentDidMount() {
    const { innerRef } = this.props;

    if (innerRef) {
      // Expose the class instance
      innerRef(this);
    }
  }

  componentDidUpdate(prevProps: CompletePlayerControllerPropType) {
    const { isVisible } = this.props;
    const { isVisible: prevIsVisible } = prevProps;

    if (!isVisible && isVisible !== prevIsVisible) {
      this.setPlayerOptionsVisibility(false);
    }
  }

  componentWillUnmount() {
    const { innerRef } = this.props;

    if (innerRef) {
      innerRef(null);
    }

    this.resetOptionsTimer();
  }

  resetOptionsTimer = () => {
    if (this.optionsTimer) {
      clearTimeout(this.optionsTimer);
      this.optionsTimer = null;
    }
  };

  startOptionsTimer = () => {
    this.resetOptionsTimer();
    this.optionsTimer = setTimeout(this.closePlayerOptions, OPTIONS_TIMEOUT);
  };

  handlePlayerOptionsOnMouseEnter = () => {
    this.resetOptionsTimer();
  };

  handlePlayerOptionsOnMouseLeave = () => {
    // Start a timer to allow the user to move the mouse cursor a short time outside the options element
    this.startOptionsTimer();
  };

  closePlayerOptions = () => {
    this.resetOptionsTimer();
    this.setPlayerOptionsVisibility(false);
  };

  setPlayerOptionsVisibility = (value: boolean) => {
    this.setState({ areOptionsVisible: value });
  };

  handleOnClickOverlay = () => {
    this.setPlayerOptionsVisibility(false);
  };

  handlePictoCogOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { areOptionsVisible } = this.state;
    this.setPlayerOptionsVisibility(!areOptionsVisible);
  };

  handleChannelZapperChannelChanged = (channel: NETGEM_API_CHANNEL) => {
    const { onChannelChanged } = this.props;

    onChannelChanged(channel);
  };

  handlePlayInMargin = (value: boolean) => {
    this.setState({ playingInMargin: value });
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  goToPreviousChannel = () => {
    const { channelZapper } = this;

    if (channelZapper) {
      channelZapper.goToPreviousChannel();
    }
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  goToNextChannel = () => {
    const { channelZapper } = this;

    if (channelZapper) {
      channelZapper.goToNextChannel();
    }
  };

  renderProgressBar = (): React.Node => {
    const {
      bufferedTimeRanges,
      contentType,
      currentItem,
      duration,
      endMargin,
      isTimeshiftEnabled,
      isTrailer,
      itemType,
      liveBufferLength,
      onLiveProgressBarSeekChanged,
      onStandardProgressBarSeekChanged,
      playheadPosition,
      realEnd,
      realStart,
      startMargin,
      timeshift,
      totalDuration,
      userViewEndOffset,
      userViewStartOffset,
    } = this.props;

    if (!currentItem && !isTrailer && itemType !== ExtendedItemType.OfflineContent) {
      return <EmptyProgressBar />;
    }

    const liveStartTime = currentItem?.startTime ?? 0;
    const liveEndTime = currentItem?.endTime ?? 0;

    let progressBarElement = null;

    if (contentType === ContentType.Live) {
      // Live
      progressBarElement = (
        <LiveProgressBar
          isTimeshiftEnabled={isTimeshiftEnabled}
          liveBufferLength={liveBufferLength}
          liveProgramEndTime={liveEndTime}
          liveProgramStartTime={liveStartTime}
          onSeekChanged={onLiveProgressBarSeekChanged}
          playheadPosition={playheadPosition}
          timeshift={timeshift}
        />
      );
    } else {
      // Static or live recording
      progressBarElement = (
        <StandardProgressBar
          bufferedTimeRanges={bufferedTimeRanges}
          duration={duration}
          endMargin={endMargin}
          endTime={liveEndTime}
          isLiveRecording={contentType === ContentType.LiveRecording}
          onPlayingInMargin={this.handlePlayInMargin}
          onSeekChanged={onStandardProgressBarSeekChanged}
          playheadPosition={playheadPosition}
          realEnd={realEnd}
          realStart={realStart}
          startMargin={startMargin}
          startTime={liveStartTime}
          totalDuration={totalDuration}
          userViewEndOffset={userViewEndOffset}
          userViewStartOffset={userViewStartOffset}
        />
      );
    }

    return progressBarElement;
  };

  renderDownloadPicto = (): React.Node => {
    const { contentType, isDebugModeEnabled, itemType, onDownloadClick } = this.props;

    if (!isDebugModeEnabled || contentType === ContentType.Live || itemType === ExtendedItemType.OfflineContent) {
      return null;
    }

    return <PictoDownload aria-label={Localizer.localize('accessibility.player.download')} onClick={onDownloadClick} />;
  };

  renderOptionsPicto = (): React.Node => {
    const { audioMediaInfo, isMaxBitrateAllowed, itemType, subtitlesMediaInfo } = this.props;
    const { areOptionsVisible } = this.state;

    /*
     * Options are enabled if at least one of these 3 things happens:
     *  - Green streaming is allowed
     *  - There are more than 1 audio track
     *  - There are at least 1 subtitles track
     */
    const areOptionsEnabled = (isMaxBitrateAllowed && itemType !== ExtendedItemType.OfflineContent) || audioMediaInfo.length > 1 || subtitlesMediaInfo.length > 0;

    const onClickHandler = areOptionsEnabled ? this.handlePictoCogOnClick : undefined;

    return (
      <PictoCog
        aria-controls='options-menu'
        aria-expanded={areOptionsVisible}
        aria-haspopup
        aria-label={Localizer.localize('accessibility.player.options.button')}
        isDisabled={!areOptionsEnabled}
        onClick={onClickHandler}
        onMouseEnter={this.handlePlayerOptionsOnMouseEnter}
      />
    );
  };

  renderFullScreenPicto = (): React.Node => {
    const { isInFullscreen, onEnterFullScreenClick, onExitFullScreenClick } = this.props;

    return isInFullscreen ? (
      <PictoCompress aria-label={Localizer.localize('accessibility.player.exit_fullscreen')} onClick={onExitFullScreenClick} />
    ) : (
      <PictoExpand aria-label={Localizer.localize('accessibility.player.enter_fullscreen')} onClick={onEnterFullScreenClick} />
    );
  };

  handleOnRestartClick = (): React.Node => {
    const { contentType, onLiveRestart, onStandardProgressBarSeekChanged, startMargin, userViewStartOffset } = this.props;

    if (contentType === ContentType.Live) {
      onLiveRestart();
    } else {
      onStandardProgressBarSeekChanged(Math.max(userViewStartOffset, startMargin));
    }
  };

  renderRestartButton = (): React.Node => {
    const { channelId, channels, contentType, isControllerEnabled } = this.props;

    if (process.env.REACT_APP_ID !== APPLICATION_ID.Post || !channelHasStartover(channels, channelId) || !channelHasCatchup(channels, channelId) || contentType === ContentType.Static) {
      // Restart button should only be present for POST + live content + channel has startover and catchup
      return null;
    }

    return <PictoReplay aria-label={Localizer.localize('accessibility.player.restart')} className='restartPicto' isDisabled={!isControllerEnabled} onClick={this.handleOnRestartClick} />;
  };

  renderNextButton = (): React.Node => {
    const { contentType, continuousWatching, isControllerEnabled, location, onNextEpisodeClick, seriesMetadata } = this.props;

    const locType = getLocationType(location?.id);

    // TODO: Remove this code when everything works in all apps
    if (process.env.REACT_APP_ID !== APPLICATION_ID.FranceChannel) {
      // Current scope is limited to FranceChannel
      return null;
    }

    // Hide "next" button for singles, live and SVOD with a non-Netgem authority
    if (contentType === ContentType.Live || contentType === ContentType.LiveRecording || seriesMetadata === null) {
      return null;
    }

    if (!continuousWatching) {
      return null;
    }

    // Disabled if all controls are disabled or if it's not a (S)VOD series
    const isDisabled = !isControllerEnabled || continuousWatching.isLastEpisode || locType === ItemLocationKind.App;

    return <PictoNext aria-label={Localizer.localize('accessibility.player.next_episode')} className='nextPicto' isDisabled={isDisabled} onClick={onNextEpisodeClick} />;
  };

  renderControlButtons = (): React.Node => {
    const { isBlackOutEnabled, isControllerEnabled, isMuted, isTrailer, itemType, onInfoClick, onVolumeChanged, onVolumeClick, volume } = this.props;

    return (
      <div className='buttons'>
        <div className='leftButtonsContainer'>
          {this.renderRestartButton()}
          {this.renderSkipBackwardButton()}
          {this.renderPlayOrPauseButton()}
          {this.renderSkipForwardButton()}
          {this.renderNextButton()}
          {this.renderRecordButton()}
          <PlayerVolume
            isDisabled={!isControllerEnabled || isBlackOutEnabled}
            isMuted={isMuted}
            onVolumeClickCallback={onVolumeClick}
            onVolumeControllerClickCallback={onVolumeChanged}
            volume={volume}
          />
        </div>
        <div className='rightButtonsContainer'>
          {this.renderDownloadPicto()}
          {this.renderOptionsPicto()}
          <PictoInfo aria-label={Localizer.localize('accessibility.player.more_info')} isDisabled={isTrailer || itemType === ExtendedItemType.OfflineContent} onClick={onInfoClick} />
          {this.renderFullScreenPicto()}
        </div>
      </div>
    );
  };

  renderLiveIndicator = (): React.Node => {
    const { contentType, currentItem, isLiveRecording, onGoToLiveClick, timeshift } = this.props;

    if (contentType !== ContentType.Live || !currentItem) {
      return null;
    }

    const isLive = timeshift <= TIMESHIFT_THRESHOLD;
    const { endTime, startTime } = currentItem;

    if ((!startTime && endTime) || (startTime && !endTime)) {
      return null;
    }

    const recordingPicto = isLiveRecording ? (
      <div className='liveRecording'>
        <PictoRecord className='recording live' hasBackground />
        <div className='text'>{Localizer.localize('common.status.live_recording')}</div>
      </div>
    ) : null;

    return (
      <>
        <StatusPicto
          className={clsx(!isLive && 'notLive')}
          forcedText={isLive ? undefined : Localizer.localize('player.back_to_live')}
          item={currentItem}
          onClick={isLive ? undefined : onGoToLiveClick}
        />
        {recordingPicto}
      </>
    );
  };

  renderTitleElement = (liveIndicator: React.Node): React.Node => {
    const { isProgramInfoHidden, isTrailer, seriesEpisodeText, seriesMetadata, title } = this.props;
    const seriesTitle = getTitle(seriesMetadata, Localizer.language);
    const episodeTitle = !isTrailer && seriesEpisodeText && title && !isEpisodeIndexInTitle(title) ? `${seriesEpisodeText} - ${title}` : title;

    if (isProgramInfoHidden) {
      return null;
    }

    const mainLineTitle = <div className='title main'>{seriesTitle || episodeTitle || Localizer.localize('player.no_program_info')}</div>;

    const subLineTitle = seriesTitle && episodeTitle && seriesTitle !== episodeTitle ? <div className='title'>{episodeTitle}</div> : null;

    MediaController.setTitles(episodeTitle, seriesTitle);

    return (
      <div className={clsx('titlesAndStatus', subLineTitle && 'multiline')}>
        <div className='mainLine'>
          {mainLineTitle}
          {liveIndicator}
        </div>
        {subLineTitle}
      </div>
    );
  };

  renderChannelImage = (): React.Node => {
    const { channelImageUrl, channelName, contentType } = this.props;

    if (contentType === ContentType.Live || channelImageUrl === '') {
      // No channel image for live program (channel list instead), trailer and VOD
      return null;
    }

    return (
      <div className='channelImage'>{channelImageUrl ? <img alt={Localizer.localize('accessibility.navigation.channel_logo', { channelName })} draggable={false} src={channelImageUrl} /> : null}</div>
    );
  };

  renderPictoLeaf = (): React.Node => {
    const { isBitrateLimited } = this.props;

    if (!isBitrateLimited) {
      return null;
    }

    return <PictoLeaf aria-label={Localizer.localize('accessibility.player.options.green_streaming')} className='leaf' />;
  };

  renderVideoQuality = (): React.Node => {
    const { videoQuality } = this.props;

    return (
      <div className='videoQuality'>
        {videoQuality !== Definition.SD ? (
          <div>
            <span className='screenReaderOnly'>{Localizer.localize('accessibility.content.quality_hd')}</span>
            <span aria-hidden>{videoQuality as string}</span>
          </div>
        ) : null}
        {this.renderPictoLeaf()}
      </div>
    );
  };

  getDisplayedDuration = (): number => {
    const { duration, realEnd, realStart, totalDuration } = this.props;
    let displayedDuration = 0;

    if (duration > 0 && duration < Infinity) {
      displayedDuration = duration;
    } else if (totalDuration > 0) {
      displayedDuration = totalDuration;
    }

    displayedDuration -= realStart + realEnd;
    if (displayedDuration < 0) {
      displayedDuration = 0;
    }

    return displayedDuration;
  };

  renderTimeInfo = (): React.Node => {
    const { contentType, currentItem, duration, isProgramInfoHidden, playheadPosition, realStart } = this.props;
    const { playingInMargin } = this.state;

    if (isProgramInfoHidden) {
      return null;
    }

    if (contentType === ContentType.Live) {
      if (!currentItem) {
        return null;
      }

      // Live program: display start and end times
      const { endTime, startTime } = currentItem;

      const programStart = formatTimeFromSeconds(startTime);
      const programEnd = formatTimeFromSeconds(endTime);

      return (
        <div className='timeInfo'>
          <span className='screenReaderOnly'>{Localizer.localize('accessibility.player.program_live_start_end', { end: programEnd, start: programStart })}</span>
          <span aria-hidden>{programStart}</span>
          <span aria-hidden className='timeSeparator'>
            -
          </span>
          <span aria-hidden>{programEnd}</span>
        </div>
      );
    }

    if (duration === 0) {
      return null;
    }

    // Static program: display current position and total duration
    const displayedSeekPosition = playheadPosition - realStart;
    const displayedDuration = this.getDisplayedDuration();

    return (
      <div className='timeInfo'>
        <span className='screenReaderOnly'>
          {Localizer.localize('accessibility.player.program_static_position_duration', {
            duration: formatSecondsForScreenReader(displayedDuration),
            position: formatSecondsForScreenReader(displayedSeekPosition),
          })}
        </span>
        <span aria-hidden className={clsx('first', playingInMargin && 'margin')}>
          {formatSecondsToHHMMSS(displayedSeekPosition)}
        </span>
        <span aria-hidden className='timeSeparator'>
          /
        </span>
        <span aria-hidden>{formatSecondsToHHMMSS(displayedDuration)}</span>
      </div>
    );
  };

  renderOptions = (): React.Node => {
    const { audioMediaInfo, itemType, onNewAudioTrackSelected, onNewSubtitlesTrackSelected, selectedAudioMediaInfo, selectedSubtitlesMediaInfo, subtitlesMediaInfo } = this.props;
    const { areOptionsVisible } = this.state;

    if (!areOptionsVisible) {
      return null;
    }

    return (
      <Options
        audioMediaInfo={audioMediaInfo}
        itemType={itemType}
        onMouseEnter={this.handlePlayerOptionsOnMouseEnter}
        onMouseLeave={this.handlePlayerOptionsOnMouseLeave}
        onNewAudioTrackSelected={onNewAudioTrackSelected}
        onNewSubtitlesTrackSelected={onNewSubtitlesTrackSelected}
        selectedAudioMediaInfo={selectedAudioMediaInfo}
        selectedSubtitlesMediaInfo={selectedSubtitlesMediaInfo}
        subtitlesMediaInfo={subtitlesMediaInfo}
      />
    );
  };

  renderChannelZapper = (): React.Node => {
    const { channelId, contentType } = this.props;

    if (contentType !== ContentType.Live) {
      return null;
    }

    return (
      <ChannelZapper
        channelId={channelId}
        onChannelChangedCallback={this.handleChannelZapperChannelChanged}
        ref={(instance: ChannelZapperHandle | null) => {
          this.channelZapper = instance;
        }}
      />
    );
  };

  renderRecordButton = (): React.Node => {
    const { contentType, currentItem, isBlackOutEnabled, isControllerEnabled, location, programMetadata, seriesMetadata, tileType, timeshift } = this.props;

    if (!currentItem || !programMetadata) {
      return null;
    }

    const { locType } = currentItem;
    if (locType === ItemLocationKind.Recording) {
      // User is not allowed to delete a recording from the player when they're playing it
      return null;
    }

    const broadcastStatus = contentType === ContentType.Live && timeshift <= TIMESHIFT_THRESHOLD ? BroadcastStatus.Live : BroadcastStatus.Past;

    return (
      <RecordButton
        broadcastStatus={broadcastStatus}
        context={ContextKind.Player}
        displayMode={DisplayMode.Picto}
        isDisabled={!isControllerEnabled || isBlackOutEnabled}
        item={currentItem}
        location={location}
        previewCatchupScheduledEventId={null}
        programMetadata={programMetadata}
        seriesMetadata={seriesMetadata}
        tileType={tileType}
      />
    );
  };

  renderSkipBackwardIcon = (controlLevel: ControlLevel): React.Node => {
    switch (controlLevel) {
      case ControlLevel.Level0:
        return <PictoRewind10 />;

      case ControlLevel.Level1:
        return <PictoRewind30 />;

      case ControlLevel.Level2:
        return <PictoRewind60 />;
    }
  };

  renderSkipBackwardButton = (): React.Node => {
    const { contentType, controlLevel, isControllerEnabled, isTimeshiftEnabled, onSkipBackwardClick } = this.props;

    const isDisabled = !isControllerEnabled || (contentType === ContentType.Live && !isTimeshiftEnabled);

    let ariaLabel = Localizer.localize('accessibility.player.backward10');
    if (controlLevel === ControlLevel.Level1) {
      ariaLabel = Localizer.localize('accessibility.player.backward30');
    } else if (controlLevel === ControlLevel.Level2) {
      ariaLabel = Localizer.localize('accessibility.player.backward60');
    }

    return (
      <button aria-label={ariaLabel} className='skipPicto' disabled={isDisabled} onClick={onSkipBackwardClick} type='button'>
        {this.renderSkipBackwardIcon(controlLevel)}
      </button>
    );
  };

  renderSkipForwardIcon = (controlLevel: ControlLevel): React.Node => {
    switch (controlLevel) {
      case ControlLevel.Level0:
        return <PictoForward10 />;

      case ControlLevel.Level1:
        return <PictoForward30 />;

      case ControlLevel.Level2:
        return <PictoForward60 />;
    }
  };

  renderSkipForwardButton = (): React.Node => {
    const { contentType, controlLevel, isControllerEnabled, isTimeshiftEnabled, onSkipForwardClick, timeshift } = this.props;

    const isDisabled = !isControllerEnabled || (contentType === ContentType.Live && (timeshift <= TIMESHIFT_THRESHOLD || !isTimeshiftEnabled));

    let ariaLabel = Localizer.localize('accessibility.player.forward10');
    if (controlLevel === ControlLevel.Level1) {
      ariaLabel = Localizer.localize('accessibility.player.forward30');
    } else if (controlLevel === ControlLevel.Level2) {
      ariaLabel = Localizer.localize('accessibility.player.forward60');
    }

    return (
      <button aria-label={ariaLabel} className='skipPicto' disabled={isDisabled} onClick={onSkipForwardClick} type='button'>
        {this.renderSkipForwardIcon(controlLevel)}
      </button>
    );
  };

  renderPlayOrPauseButton = (): React.Node => {
    const { contentType, isControllerEnabled, isPlaying, isTimeshiftEnabled, onPauseClick, onPlayClick } = this.props;

    const isDisabled = !isControllerEnabled || (contentType === ContentType.Live && !isTimeshiftEnabled);
    const label = isPlaying ? Localizer.localize('accessibility.player.pause') : Localizer.localize('accessibility.player.play');
    const onClickHandler = isPlaying ? onPauseClick : onPlayClick;

    return (
      <button aria-label={label} className='' disabled={isDisabled} onClick={onClickHandler} type='button'>
        {isPlaying ? <PictoPause /> : <PictoBigPlay className='play' />}
      </button>
    );
  };

  render(): React.Node {
    const { isVisible, item, onMouseEnter, onMouseLeave } = this.props;

    const liveIndicator = this.renderLiveIndicator();

    return (
      <div className={clsx('videoController', isVisible && 'visible')} onClick={this.handleOnClickOverlay} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {this.renderChannelZapper()}
        <div className='controller'>
          <div className='textInfo'>
            {this.renderChannelImage()}
            {this.renderTitleElement(liveIndicator)}
            <div className='rightDetails'>
              <div className='rowWrapper'>
                {item ? renderProgramParentalGuidance(item) : null}
                {this.renderVideoQuality()}
              </div>
              {this.renderTimeInfo()}
            </div>
          </div>
          {this.renderProgressBar()}
          {this.renderControlButtons()}
          {this.renderOptions()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxPlayerControllerReducerStateType => {
  return {
    channels: state.appConfiguration.deviceChannels,
    isBitrateLimited: getBooleanSetting(state.ui.settings, Setting.GreenStreaming),
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    isMaxBitrateAllowed: state.appConfiguration.isMaxBitrateAllowed,
  };
};

const PlayerController: React.ComponentType<PlayerControllerPropType> = connect(mapStateToProps)(PlayerControllerView);

export { PlayerControllerView };
export default PlayerController;
